import type {
  Capability,
  CapabilityStatus,
  PaymentAccount,
} from "@/domain/payment-account/types";

export interface CapabilityPayload {
  status: string | null;
  statusDetails: { code: string | null; resolution: string | null }[] | null;
}

export interface AccountRequirementPayload {
  description: string;
  awaitingActionFrom: string | null;
  deadlineStatus: string | null;
  restrictsCapabilities: { capability: string; configuration: string | null }[] | null;
  errors: { code: string | null; description: string | null }[] | null;
}

export interface PaymentAccountPayload {
  businessId: number;
  accountId: string | null;
  canTrade: boolean;
  syncedAt: string | null;
  syncError: string | null;
  capabilities: {
    cardPayments: CapabilityPayload;
    transfers: CapabilityPayload;
    payouts: CapabilityPayload;
  };
  requirements: AccountRequirementPayload[] | null;
}

const knownStatuses = ["active", "pending", "restricted", "unsupported"] as const;

/** `??` only catches null/undefined, and every list here comes from a `jsonb`
 *  column that promises nothing about its shape — an object reaching `.map`
 *  is exactly the error that put this ticket here. Guarded again on this side
 *  even though `lastgo-api` (`feat/five-small-corrections`) now does the same:
 *  a mapper is where a payload stops being someone else's promise. */
function toArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

/** A list member that is not an object (`errors: [null]`) throws on its first
 *  field read and takes the whole section down with it. */
function toObjects<T extends object>(value: T[] | null | undefined): T[] {
  return toArray(value).filter((member): member is T => typeof member === "object" && member !== null);
}

/** A status this build does not know is not a status it may reassure anyone
 *  about — it reads as absent, which is the state that asks for a human. */
function toCapabilityStatus(status: string | null): CapabilityStatus {
  return knownStatuses.find((known) => known === status) ?? null;
}

function toCapability(payload: CapabilityPayload): Capability {
  return {
    status: toCapabilityStatus(payload.status),
    statusDetails: toObjects(payload.statusDetails),
  };
}

function toDate(value: string | null): Date | null {
  if (value === null) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function payloadToPaymentAccount(payload: PaymentAccountPayload): PaymentAccount {
  return {
    businessId: payload.businessId,
    accountId: payload.accountId,
    canTrade: payload.canTrade,
    syncedAt: toDate(payload.syncedAt),
    syncError: payload.syncError === "STRIPE_UNREACHABLE" ? "STRIPE_UNREACHABLE" : null,
    capabilities: {
      cardPayments: toCapability(payload.capabilities.cardPayments),
      transfers: toCapability(payload.capabilities.transfers),
      payouts: toCapability(payload.capabilities.payouts),
    },
    requirements: toObjects(payload.requirements).map((requirement) => ({
      description: requirement.description,
      awaitingActionFrom: requirement.awaitingActionFrom,
      deadlineStatus: requirement.deadlineStatus,
      restrictsCapabilities: toObjects(requirement.restrictsCapabilities),
      errors: toObjects(requirement.errors),
    })),
  };
}
